import { useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';
import moment from 'moment';
import {
  KeyboardAvoidingView,
  Linking,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { PressableNeolexon } from '@/components/PressableNeolexon';
import { useResponsiveFontSize } from '@/components/Util/reactResponsiveDimensions';
import colors, { appTheme } from '@/config/colors';
import { Text } from '@/components/Text';
import api from '../../api/axios';
import { DigaMessages } from './DigaMessages';
import { Icon } from '@/components/Icon';
import { logError } from '@/utils/logHandling';
import { TextLink } from '@/components/TextLink';
import { DigaCodeInput } from './DigaCodeInput';
import { ExpiredDigaCodes } from './ExpiredDigaCodes';
import { styles } from './styles';
import { showDigaPatientWarning } from '@/utils/licenseCheck';
import { getLicenseStatus, LicenseStatus } from '@/api/services/user';

export interface DigaCodeItem {
  digaCode: string;
  status: string;
  userExpireInform7d: boolean;
  userExpireInform14d: boolean;
  userExpireInform30d: boolean;
  invoiceSend: boolean;
  expireAt: string;
  isActive: boolean;
  dayOfServiceProvision: string;
  user: string;
  createdAt: Date;
  updatedAt: Date;
}

export function DigaCodes() {
  const [isModalVisible, setModalVisible] = useState(false);
  const [
    isDeactivationPostponeConsentModalVisible,
    setDeactivationPostponeConsentModalVisible,
  ] = useState(false);
  const [consentId, setConsentId] = useState(null);
  const [isModalErrorVisible, setModalErrorVisible] = useState(false);
  const [isModalErrorAPIVisible, setModalErrorAPIVisible] = useState(false);
  const [isModalNetworkErrorVisible, setModalNetworkErrorVisible] =
    useState(false);
  const [isScreenInputVisible, setScreenInputVisible] = useState(false);
  const [isScreenExpiredCodesVisible, setScreenExpiredCodesVisible] =
    useState(false);
  const [
    isScreenNoInternetConnectionVisible,
    setScreenNoInternetConnectionVisible,
  ] = useState(false);
  const [isNetworkError, setNetworkError] = useState(false);
  const [isExpiredCodesExpanded, setIsExpiredCodesExpanded] = useState(false);
  const [licenseStatus, setLicenseStatus] = useState<LicenseStatus | null>(
    null,
  );
  const germanDateFormat = 'DD.MM.YYYY';

  const [expiredCodesList, setExpiredCodesList] = useState<
    Omit<
      DigaCodeItem,
      | 'status'
      | 'userExpireInform7d'
      | 'userExpireInform14d'
      | 'userExpireInform30d'
      | 'invoiceSend'
      | 'isActive'
      | 'dayOfServiceProvision'
      | 'user'
      | 'createdAt'
      | 'updatedAt'
    >[]
  >([]);
  const [activeCodes, setActiveCodes] = useState<string[]>([]);
  const [getExpirationDate, setExpirationDate] = useState<string>('');

  const responsiveFontSize = useResponsiveFontSize(1);
  const getResponsiveFontSize = (factor) => {
    return responsiveFontSize * factor;
  };

  let responsiveStyles = StyleSheet.create({
    buttonStyle: {
      borderRadius: 8,
      backgroundColor: appTheme.primary,
      padding: getResponsiveFontSize(0.5),
    },
    buttonTitleStyle: {
      fontSize: getResponsiveFontSize(1.1),
      fontWeight: '700',
    },
  });

  const {
    control,
    clearErrors,
    handleSubmit,
    reset,
    formState: { isSubmitted, isValid },
  } = useForm({
    mode: 'all',
    defaultValues: {
      digaCode: '',
    },
  });

  const resetErrorModalVisibility = () => {
    setModalErrorVisible(false);
    clearErrors();
  };

  const digaRegExPattern = /^[2-7a-zA-Z]{16}$/g;
  const digaCharacters = (value: string) => {
    const digaCodeRegEx = new RegExp(digaRegExPattern);
    const isValidated = digaCodeRegEx.test(value);
    if (isValidated) {
      clearErrors();
    }
    return isValidated;
  };

  const isCodeExpired = (dateToCheck: Date) => {
    return moment(new Date()).isAfter(dateToCheck);
  };

  const extractExpiredCodes = (codesInfo) => {
    const expiredCodes = codesInfo.filter((codeInfo) =>
      isCodeExpired(codeInfo.expireAt),
    );
    setScreenExpiredCodesVisible(expiredCodes.length > 0);
    const list = expiredCodes.map((expiredValue) => {
      return {
        digaCode: expiredValue.digaCode,
        expireAt: moment(expiredValue.expireAt).format(germanDateFormat),
      };
    });
    setExpiredCodesList(list);
  };

  const initDigaCodeList = async () => {
    api
      .get('/api/digacodes/list')
      .then((response) => response.data)
      .then((codesInfo: DigaCodeItem[]) => {
        const activeCodes = codesInfo.filter((codeInfo) => codeInfo.isActive);
        if (activeCodes.length > 0) {
          setActiveCodes(activeCodes.map((code) => code.digaCode));
          setExpirationDate(
            moment(activeCodes[activeCodes.length - 1].expireAt).format(
              germanDateFormat,
            ),
          );
          clearErrors();
          setScreenInputVisible(false);
        } else {
          setScreenInputVisible(true);
        }

        extractExpiredCodes(codesInfo);
      })
      .catch(() => {
        setNetworkError(true);
        setModalNetworkErrorVisible(true);
        setScreenNoInternetConnectionVisible(true);
      });

    const licenseStatus = await getLicenseStatus();
    setLicenseStatus(licenseStatus);
  };

  const verifyAndRedeemDigaCode = async (currentCode: string) => {
    try {
      const response = await api.post('/api/digacodes/verify', {
        digaCode: currentCode,
      });
      if (
        response.data &&
        response.data.deactivationPostponeConsent &&
        !response.data.deactivationPostponeConsent.givenConsent
      ) {
        setDeactivationPostponeConsentModalVisible(true);
        setConsentId(response.data.deactivationPostponeConsent._id);
      } else {
        setModalVisible(true);
      }
      reset();
      initDigaCodeList();
    } catch (e) {
      if (e.toString() === 'Error: Network Error') {
        setModalNetworkErrorVisible(true);
      } else {
        setModalErrorAPIVisible(true);
      }
    }
  };

  const onSubmit = (submission: { digaCode: string }) => {
    if (digaCharacters(submission.digaCode)) {
      !verifyAndRedeemDigaCode(submission.digaCode) &&
        setModalErrorAPIVisible(true);
    } else {
      setModalErrorVisible(true);
    }
  };

  const onRefreshScreen = () => {
    setNetworkError(false);
    initDigaCodeList();
  };

  useEffect(() => {
    initDigaCodeList();
  }, []);

  const getBorderStyle = function (digaCode: string) {
    const digaRegEx = new RegExp(digaRegExPattern);
    let isValid = digaRegEx.test(digaCode);

    if (isValid) {
      return styles.inputBorderAccepted;
    } else if ((isSubmitted || digaCode.length === 16) && !isValid) {
      return styles.inputBorderError;
    } else {
      return {};
    }
  };

  return (
    <KeyboardAvoidingView style={styles.fullHeight} behavior="height">
      <ScrollView
        style={{ flex: 1, width: '100%' }}
        contentContainerStyle={{
          alignItems: 'center',
          flexGrow: 1,
        }}
      >
        <View style={styles.container}>
          {isScreenInputVisible ? (
            <View>
              <DigaCodeInput
                label="Rezeptcode von Ihrer Krankenkasse eingeben:"
                isError={!isValid && isSubmitted}
                control={control}
                getBorderStyle={getBorderStyle}
                digaCharacters={digaCharacters}
                responsiveStyles={responsiveStyles}
                handleSubmit={handleSubmit}
                onSubmit={onSubmit}
              />
              <MoreInfo />
              <HelpAndContact />
            </View>
          ) : null}

          {activeCodes.length === 1 ? (
            <>
              <View style={styles.digaWrapper}>
                <View>
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    {licenseStatus && showDigaPatientWarning(licenseStatus) ? (
                      <Icon
                        name="alertTriangle"
                        size={18}
                        color={colors.yellow}
                      />
                    ) : null}
                    <Text style={[styles.information, styles.bold]}>
                      {'Ihre aktuelle Lizenz endet am: '}
                      {getExpirationDate}
                    </Text>
                  </View>
                  <Text style={[styles.information, styles.bold]}>
                    {'Verwendeter Rezeptcode: '}
                    {activeCodes[0]}
                  </Text>
                </View>
              </View>
              <View style={styles.cardContainer}>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <Icon
                    name="circleInfo"
                    size={24}
                    style={{ marginRight: 10 }}
                  />
                  <Text style={styles.cardLabel}>
                    Sie können hier einen neuen Rezeptcode vorplanen{' '}
                  </Text>
                </View>
                <Text style={{ fontSize: 16, marginVertical: 12 }}>
                  Sie müssen nicht warten, bis Ihre Lizenz ausläuft.{' '}
                  <TextLink href="https://neolexon.de/kostenerstattung/aphasie-app-diga/">
                    Beantragen Sie einfach einen neuen Rezeptcode bei Ihrer
                    Krankenkasse.{' '}
                  </TextLink>
                  Dabei besteht keine Obergrenze, wie oft Sie einen neuen
                  Rezeptcode beantragen können.
                </Text>
              </View>
              <DigaCodeInput
                control={control}
                label="Rezeptcode vorplanen"
                description="Haben Sie schon einen neuen Rezeptcode von Ihrer Krankenkasse? Geben Sie ihn hier ein. Ihre Lizenz läuft dann automatisch weiter – ohne Unterbrechung."
                getBorderStyle={getBorderStyle}
                digaCharacters={digaCharacters}
                responsiveStyles={responsiveStyles}
                handleSubmit={handleSubmit}
                onSubmit={onSubmit}
                isSecondDigaCodeInput
                isError={!isValid && isSubmitted}
              />
              <HelpAndContact />
            </>
          ) : null}

          {activeCodes.length > 1 ? (
            <HasTwoDigaCodes endDate={getExpirationDate} codes={activeCodes} />
          ) : null}

          {isScreenExpiredCodesVisible && expiredCodesList.length > 0 ? (
            <ExpiredDigaCodes
              expiredCodesList={expiredCodesList}
              isExpiredCodesExpanded={isExpiredCodesExpanded}
              setIsExpiredCodesExpanded={setIsExpiredCodesExpanded}
            />
          ) : null}

          {isScreenNoInternetConnectionVisible && isNetworkError ? (
            <View style={styles.errorWrapper}>
              <Text style={styles.errorInternetHeader}>Fehler beim Laden</Text>
              <Text style={styles.errorInternetSubtitle}>
                Bitte überprüfen Sie Ihre Internetverbindung
              </Text>
              <PressableNeolexon onPress={onRefreshScreen}>
                <Text style={styles.errorInternetLink}>wiederholen</Text>
              </PressableNeolexon>
            </View>
          ) : null}

          <DigaMessages
            isModalVisible={isModalVisible}
            isSecondDigaCode={activeCodes.length > 1}
            setModalVisible={setModalVisible}
            isDeactivationPostponeConsentModalVisible={
              isDeactivationPostponeConsentModalVisible
            }
            setDeactivationPostponeConsentModalVisible={
              setDeactivationPostponeConsentModalVisible
            }
            isModalErrorVisible={isModalErrorVisible}
            setModalErrorVisible={resetErrorModalVisibility}
            isModalErrorAPIVisible={isModalErrorAPIVisible}
            setModalErrorAPIVisible={setModalErrorAPIVisible}
            isModalInternetErrorVisible={isModalNetworkErrorVisible}
            setModalInternetErrorVisible={setModalNetworkErrorVisible}
            consentId={consentId}
          />
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

const HelpAndContact = () => {
  const handleEmailPress = async () => {
    try {
      await Linking.openURL('mailto:info@neolexon.de');
    } catch (error) {
      logError('Error opening email:', error);
    }
  };

  const handlePhonePress = async () => {
    try {
      await Linking.openURL('tel:+49 (0)89 24886 4440');
    } catch (error) {
      logError('Error opening email:', error);
    }
  };

  return (
    <View style={styles.cardContainer}>
      <Text style={styles.cardLabel}>Benötigen Sie Hilfe?</Text>
      <Text style={styles.secondInfoText}>
        Bei Fragen oder Schwierigkeiten rund um die Beantragung oder den Erhalt
        eines Rezeptcodes helfen wir Ihnen gerne persönlich weiter.
      </Text>
      <View style={{ marginTop: 10, gap: 10 }}>
        <Pressable onPress={handleEmailPress}>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <Icon style={styles.icon} name="envelope" />
            <Text style={{ fontSize: 18 }}>info@neolexon.de</Text>
          </View>
        </Pressable>
        <Pressable onPress={handlePhonePress}>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <Icon style={styles.icon} name="phone" />
            <Text style={{ fontSize: 18 }}>+49 (0)89 24886 4440</Text>
          </View>
        </Pressable>
      </View>
    </View>
  );
};

const MoreInfo = () => {
  return (
    <View style={styles.cardContainer}>
      <Text style={styles.cardLabel}>Sie haben noch keinen Rezeptcode? </Text>
      <Text style={styles.secondInfoText}>
        Kein Problem!{' '}
        <TextLink href="https://neolexon.de/kostenerstattung/aphasie-app-diga/">
          Hier finden Sie alle Schritte einfach erklärt.
        </TextLink>
      </Text>
    </View>
  );
};

const HasTwoDigaCodes = ({
  endDate,
  codes,
}: {
  endDate: string;
  codes: string[];
}) => {
  return (
    <View style={styles.validCodesContainer}>
      <Icon name="checkCircle" size={24} color={colors.primary} />
      <Text style={styles.validCodesText}>
        Ihre Lizenz endet am: {'\n'}
        {endDate}
      </Text>
      <Text style={{ fontSize: 18, marginTop: 10, textAlign: 'center' }}>
        Verwendete Rezeptcodes: {'\n'}
        <Text> {codes.map((code) => code).join('\n')}</Text>
      </Text>
    </View>
  );
};
